import { Controller } from "stimulus";
import tomSelect from 'tom-select';

export default class FormController extends Controller {

    connect() {
        this.connectedAt = Date.now();

        if (window.supercast) {
            window.supercast.trigger('pageload')
        }

        this.refreshSummary();

        // Re-check on a timer so a page left open re-hides the field as its publish time drifts
        // under the floor. Thirty seconds is well inside the five-minute floor.
        this.summaryTimer = setInterval(() => this.refreshSummary(), 30_000);
    }

    disconnect() {
        if (this.summaryTimer) clearInterval(this.summaryTimer);
    }

    // Compares like with like: the server-rendered data-server-now string is emitted in the same
    // zone as the published_at input's value, so both are shifted by the same browser offset when
    // parsed as local and the offset cancels. See the timezone note in the plan this shipped with.
    secondsUntilPublish() {
        if (!this.hasPublishAtInputTarget) return null;
        const publishMs = Date.parse(this.publishAtInputTarget.value);
        const anchorMs = Date.parse(this.element.dataset.serverNow);
        if (isNaN(publishMs) || isNaN(anchorMs)) return null;

        return (publishMs - (anchorMs + (Date.now() - this.connectedAt))) / 1000;
    }

    playlistSelectTargetConnected(el) {
        this.playlistTom = new tomSelect(el, {
            create: (input) => {
                return {
                    value: "new:" + input,
                    text: input
                }
            }
        });
    }

    removeTimestamp(e) {
        e.preventDefault();
        e.target.closest("[data-timestamp]").remove();
    }

    emailContentTargetConnected(el) {
        el.addEventListener('trix-change', (e) => {
            this.emailContentAlertTarget.dataset.email = e.target.editor.getDocument().toString();
            this.maybeWarn();
        });

        this.maybeWarn();
    }

    maybeWarn() {
        if (this.emailContentAlertTarget.dataset.description !== this.emailContentAlertTarget.dataset.email) {
            this.emailContentAlertTarget.classList.remove('hidden');
        }
    }

    // The single source of truth for "what release_plan is currently selected" - one of the four
    // cards, including the Premiere card, whose own radio value is rewritten between
    // premiere_now/premiere_scheduled by choosePremiereTiming below rather than ever being a
    // second card. Every other piece of derived state in this controller reads from this. A plain
    // DOM query rather than a Stimulus target list, since all four cards already have to agree on
    // this exact bare `release_plan` name with the controller's own top-level param reading - no
    // extra target needed to also make them agree with this controller.
    currentPlan() {
        const checked = this.element.querySelector('input[name="release_plan"]:checked');
        return checked ? checked.value : null;
    }

    // The "When does the watch party start?" segmented control never submits anything itself - it
    // only rewrites the Premiere card's own radio value and re-dispatches change so both
    // visibility-toggle (a plain data-action listener on that one radio) and refreshSummary (bound
    // at the form level) re-run from the new value. Never touches .checked, so the Premiere card's
    // own selected state (has-[:checked]:) is untouched by this control.
    choosePremiereTiming(event) {
        this.chooseTiming(event, this.hasPremiereReleasePlanRadioTarget && this.premiereReleasePlanRadioTarget, "premiere");
    }

    chooseAmaTiming(event) {
        this.chooseTiming(event, this.hasAmaReleasePlanRadioTarget && this.amaReleasePlanRadioTarget, "ama");
    }

    chooseTiming(event, radio, prefix) {
        if (!radio) return;

        const value = event.target.value === "scheduled" ? `${prefix}_scheduled` : `${prefix}_now`;
        if (radio.value === value) return;

        radio.value = value;
        radio.dispatchEvent(new Event("change", {bubbles: true}));
    }

    amaThreadSelectTargetConnected() {
        this.syncAmaPlan();
    }

    // The picker offers no "no thread" option, so an AMA release always links one. Two consequences
    // ride on the plan alone rather than on the picker's value:
    //
    // The picker is disabled off-plan. It still submits when merely hidden, so leaving it enabled
    // would link whichever thread happened to be showing on a Publish now - and a disabled select
    // submits nothing, which desired_ama_thread_id_for_publish reads as "leave the linking alone".
    //
    // Comments open is forced on and locked. The thread already carries its own comments, so the
    // switch says why rather than reading as a separate choice. The creator's own value is
    // remembered, so leaving the AMA plan restores it instead of leaving the switch stuck on.
    syncAmaPlan() {
        const ama = this.isAma(this.currentPlan());

        if (this.hasAmaThreadSelectTarget) this.amaThreadSelectTarget.disabled = !ama;
        if (this.hasAmaCommentsNoteTarget) this.amaCommentsNoteTarget.classList.toggle("hidden", !ama);

        if (!this.hasCommentsToggleTarget) return;
        const toggle = this.commentsToggleTarget;

        if (ama) {
            // Seeded once per lock. `checked` is only the creator's own value while the switch is
            // unlocked - on a server-rendered AMA plan it arrives already locked on, and reading it
            // then would remember the lock itself and leave Comments stuck on after switching away.
            if (toggle.dataset.restoreChecked === undefined) {
                toggle.dataset.restoreChecked = toggle.disabled
                    ? (toggle.dataset.unlockedChecked ?? "false")
                    : String(toggle.checked);
            }
            toggle.checked = true;
            this.setToggleDisabled(toggle, true);
        } else if (toggle.disabled) {
            this.setToggleDisabled(toggle, false);
            if (toggle.dataset.restoreChecked !== undefined) {
                toggle.checked = toggle.dataset.restoreChecked === "true";
                delete toggle.dataset.restoreChecked;
            }
        }

        this.paintToggleLock(toggle, ama);
    }

    // Rails' check_box emits a paired hidden field carrying the unchecked value, and it only marks
    // that field disabled when the checkbox was rendered disabled server-side. Locking client-side
    // therefore has to disable both, or auto-save skips the checkbox and submits the hidden "0"
    // alone - writing allow_comments false while the wizard is displaying it forced on. With both
    // disabled the field submits nothing at all, which is the intent: a value the wizard is forcing
    // isn't a choice to record. #publish sets it for real when the AMA thread actually links.
    setToggleDisabled(toggle, disabled) {
        toggle.disabled = disabled;

        const companion = toggle.form?.querySelector(`input[type="hidden"][name="${CSS.escape(toggle.name)}"]`);
        if (companion) companion.disabled = disabled;
    }

    // ToggleSwitchComponent renders the locked and unlocked class strings onto the track and title
    // as data attributes precisely so this swap can't drift from them. Disabling the input alone
    // isn't enough: the styling is a plain class, so without this a locked switch stays painted as
    // though it were still interactive.
    paintToggleLock(input, locked) {
        // Found by the attributes themselves rather than by walking siblings: the two elements are
        // the ones that publish these strings, and a template change that moves them would silently
        // stop the repaint if this depended on their position.
        const scope = input.closest("label") || input.parentElement;

        for (const prefix of ["track", "title"]) {
            const el = scope?.querySelector(`[data-${prefix}-locked][data-${prefix}-unlocked]`);
            const on = el?.dataset[`${prefix}Locked`];
            const off = el?.dataset[`${prefix}Unlocked`];
            if (!on || !off) continue;

            el.classList.remove(...(locked ? off : on).split(" "));
            el.classList.add(...(locked ? on : off).split(" "));
        }
    }

    // Never rely on `disabled` to blank a date: a disabled input submits nothing at all, so
    // assign_attributes never touches the stored value, and Publishable's auto_publish
    // before_validation hook would then publish off a stale date from an earlier scheduled choice.
    clearDateForNonDatedPlans(plan) {
        if (!this.hasPublishAtInputTarget) return;
        if (!this.isScheduled(plan) && this.publishAtInputTarget.value !== "") {
            this.publishAtInputTarget.value = "";
        }
    }

    // #publish's server-rendered "must be set (to a future date)" error goes stale the moment the
    // creator touches the release plan or the date - bound directly to those two change events
    // (visibility.html.erb) rather than folded into refreshSummary, which also runs on connect and
    // on the 30s timer and would otherwise wipe a freshly server-rendered error before it's seen.
    clearPublishAtError() {
        if (!this.hasPublishAtErrorTarget) return;

        this.publishAtErrorTarget.textContent = "";
    }

    // Which plans are dated, which are premieres, which are AMAs: read from the table
    // Episodes::ReleasePlan.client_map serialises onto the form, never re-listed here. Four
    // hand-written copies of "which values end in _scheduled" is what this replaces, and a fifth
    // copy on the Ruby side is how the wizard's pre-selection drifted in the first place.
    // Absent on the detail step, which renders no release-plan radios - every caller below is
    // already gated on a target that only the visibility step has.
    planFacts(plan) {
        if (!this._planFacts) {
            try {
                this._planFacts = JSON.parse(this.element.dataset.releasePlans || "{}");
            } catch (_) {
                this._planFacts = {};
            }
        }
        return this._planFacts[plan] || {};
    }

    isScheduled(plan) { return !!this.planFacts(plan).scheduled; }

    isPremiere(plan) { return !!this.planFacts(plan).premiere; }

    isAma(plan) { return !!this.planFacts(plan).ama; }

    updateSubmitLabel(plan) {
        if (!this.hasSubmitLabelTarget) return;

        const dated = this.isScheduled(plan);
        // Covers a transfer still in flight as well as Bunny processing - both defer the publish.
        const deferred = this.element.dataset.publishDeferred === "true";
        this.submitLabelTarget.textContent = deferred ? "Publish when ready" : (dated ? "Schedule" : "Publish");
    }

    // Re-run whenever a summary line connects, so a Turbo frame swap can't leave the panel
    // describing the state the previous render was in.
    summaryLineTargetConnected() {
        this.refreshSummary();
    }

    // Recomputes the whole panel from the controls' current state rather than reacting to one
    // control at a time - that's what keeps the server-rendered first paint and the client in
    // agreement. Every line's copy is already in the DOM; this only ever toggles `hidden`.
    refreshSummary() {
        const plan = this.currentPlan();
        const premiere = this.isPremiere(plan);
        const scheduled = this.isScheduled(plan);

        this.clearDateForNonDatedPlans(plan);
        this.updateSubmitLabel(plan);
        this.syncAmaPlan();

        const notifyTargets = this.hasNotifySelectTarget ? this.notifySelectTargets : [];
        const checkedNotify = notifyTargets.find((input) => input.checked);
        const notify = checkedNotify ? checkedNotify.value : null;

        const untilPublish = this.secondsUntilPublish();
        const minLead = Number(this.element.dataset.minLeadSeconds);
        const tooSoon = untilPublish !== null && untilPublish <= minLead;

        // A lead only means anything for a scheduled premiere still outside the floor. Disabled
        // as well as hidden so the control can never submit a stale value the creator can't see.
        const leadApplies = premiere && scheduled && !tooSoon;
        notifyTargets.forEach((input) => { input.disabled = !leadApplies; });
        if (this.hasNotifyFieldTarget) this.notifyFieldTarget.classList.toggle("hidden", !leadApplies);
        if (this.hasNotifyTooSoonNoteTarget) {
            this.notifyTooSoonNoteTarget.classList.toggle("hidden", !(premiere && scheduled && tooSoon));
        }

        this.refreshTimeLabels(plan);
        this.syncBackLink(plan, notify);

        if (!this.hasSummaryLineTarget) return;

        const emailAvailable = this.element.dataset.emailAvailable === "true";
        const emailOn = emailAvailable && this.hasEmailToggleTarget && this.emailToggleTarget.checked;
        const discordOn = this.hasDiscordToggleTarget && this.discordToggleTarget.checked;

        const state = {
            premiere,
            email: emailOn,
            discord: discordOn,
            notify,
            reminder: leadApplies && notify !== null && notify !== "off",
            // Number("off") is NaN, and NaN >= x is false, so "Don't notify" is naturally excluded.
            firesNow: untilPublish !== null && Number(notify) >= untilPublish,
            tooSoon,
            // Computed from the controls rather than accumulated over the loop below, so the
            // "Nobody is notified" row is just another line with its own predicate. A send blocked
            // by the daily limit still counts as a notification that was meant to happen - the
            // send-limit row says so on its own line, and this keeps the two off screen together.
            anyNotification: (leadApplies && notify !== null && notify !== "off") || emailOn || discordOn,
        };

        for (const line of this.summaryLineTargets) {
            line.classList.toggle("hidden", !this.summaryLineShown(line.dataset, state));
        }

        this.markLastVisibleRow();
    }

    // Step 2's choices exist only in this form - release_plan isn't a column, and the date, the
    // reminder lead and the AMA thread can't be auto-saved because step 2's auto-save PATCHes
    // #publish, which would schedule the episode and link the thread for real. So Back carries them
    // in its own href; the detail step hands them straight back on Next. Rewritten on every change
    // rather than read at click time, because the link is an ordinary <a> the frame navigates.
    syncBackLink(plan, notify) {
        if (!this.hasBackLinkTarget) return;

        const url = new URL(this.backLinkTarget.href, window.location.origin);
        const carried = {
            release_plan: plan,
            published_at: this.hasPublishAtInputTarget ? this.publishAtInputTarget.value : null,
            existing_ama_thread_id: this.hasAmaThreadSelectTarget ? this.amaThreadSelectTarget.value : null,
            "premiere[notification_setting]": notify,
        };

        for (const [key, value] of Object.entries(carried)) {
            if (value) {
                url.searchParams.set(key, value);
            } else {
                url.searchParams.delete(key);
            }
        }

        this.backLinkTarget.href = url.toString();
    }

    summaryLineShown(dataset, state) {
        switch (dataset.summaryLine) {
            case "reminder":            return state.reminder && dataset.summaryLead === state.notify && !state.firesNow;
            case "reminder_fires_now":  return state.reminder && dataset.summaryLead === state.notify && state.firesNow;
            case "reminder_skipped":    return state.premiere && state.tooSoon && state.notify !== null && state.notify !== "off";
            case "premiere_starts":     return state.premiere;
            case "episode_publishes":   return !state.premiere;
            case "publish_email_premiere": return state.email && state.premiere;
            case "publish_email_plain": return state.email && !state.premiere;
            // Only in the DOM at all when the account is over its limit, so this row's own
            // condition is just "the creator asked for the email".
            case "send_limit":          return state.email;
            case "discord":             return state.discord;
            case "chat_closes":         return state.premiere;
            case "nothing":             return !state.anyNotification;
            default: return false;
        }
    }

    // The connector line must stop at the last row the creator can actually see, which is not the
    // last row in the DOM: every plan's rows are present and the unselected ones are only hidden.
    markLastVisibleRow() {
        const rows = this.summaryLineTargets;
        const last = rows.filter((row) => !row.classList.contains("hidden")).pop();

        rows.forEach((row) => {
            if (row === last) {
                row.dataset.last = "true";
            } else {
                delete row.dataset.last;
            }
        });
    }

    // Absolute times, computed only ever from the picker's own parsed value - never from
    // data-server-now plus an offset. secondsUntilPublish gets away with mixing those because it is
    // a difference and the browser's zone shift cancels; an absolute label has no such cancellation.
    refreshTimeLabels(plan) {
        if (!this.hasSummaryTimeTarget) return;

        const dated = this.isScheduled(plan);
        const at = dated ? this.publishDate() : new Date();
        // A dated plan with nothing picked yet: "Now" would be a lie and a blank eyebrow reads as a
        // rendering bug, so say what it is. Matches PENDING_TIME_LABEL on the Ruby side.
        const pending = at === null;
        const trailingMs = (Number(this.element.dataset.durationSeconds) + Number(this.element.dataset.afterpartySeconds)) * 1000;

        for (const label of this.summaryTimeTargets) {
            const row = label.closest("[data-summary-time]");
            if (!row) continue;
            const kind = row.dataset.summaryTime;
            if (pending && kind !== "immediate") {
                label.textContent = "a time you pick";
                continue;
            }

            switch (kind) {
                case "publish":
                    label.textContent = dated ? this.formatTime(at) : "Now";
                    break;
                case "reminder": {
                    const lead = Number(row.dataset.summaryLead) || 0;
                    label.textContent = dated ? this.formatTime(new Date(at.getTime() - lead * 1000)) : "Now";
                    break;
                }
                // A real clock time even for a publish-now premiere - the chat closes a known
                // distance after the episode ends either way.
                case "chat_close":
                    label.textContent = this.formatTime(new Date(at.getTime() + trailingMs));
                    break;
                // "immediate" (and anything unrecognised) keeps whatever the server rendered.
            }
        }
    }

    publishDate() {
        if (!this.hasPublishAtInputTarget) return null;
        const ms = Date.parse(this.publishAtInputTarget.value);

        return isNaN(ms) ? null : new Date(ms);
    }

    // Matches TIME_FORMAT ("%a %b %-d, %-I:%M %p") on the Ruby side, so a client-rewritten eyebrow
    // is indistinguishable from a server-rendered one. Both replaces are needed: engines render
    // this as either "Thu, Aug 27, 8:00 PM" or "Thu, Aug 27 at 8:00 PM".
    formatTime(date) {
        return date
            .toLocaleString("en-US", {weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit"})
            .replace(",", "")
            .replace(" at", ",");
    }
}

FormController.targets = [
    "backLink",
    "playlistSelect",
    "emailContent",
    "emailContentAlert",
    "premiereReleasePlanRadio",
    "amaReleasePlanRadio",
    "amaThreadSelect",
    "commentsToggle",
    "amaCommentsNote",
    "emailToggle",
    "discordToggle",
    "notifyField",
    "notifySelect",
    "notifyTooSoonNote",
    "publishAtError",
    "publishAtInput",
    "submitLabel",
    "summaryLine",
    "summaryTime",
]
